import makeStyles from '@material-ui/core/styles/makeStyles';
import createMuiTheme, { Theme } from '@material-ui/core/styles/createTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import { appTheme } from 'components/AppTheme';
import { EdwardColors } from 'enums';

export const useValueStyles = makeStyles((theme: Theme) =>
  createStyles({
    number: {
      '& ::-webkit-outer-spin-button': {
        display: 'none',
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& ::-webkit-inner-spin-button': {
        display: 'none',
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& [type=number]': {
        '-moz-appearance': 'textfield',
      },
    },
    readOnly: {
      fontWeight: 500,
      background: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      color: '#666',
      '& p': {
        color: theme.palette.text.primary,
      },
    },
    outlined: {
      color: theme.palette.text.primary,
    },
  }),
);

interface SwitchProps  {
  transparent?: boolean;
  fullWidth?: boolean;
  small?: boolean;
  warning?: boolean;
}

export const useSwitchStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props:SwitchProps) => ({
      position: 'relative',
      cursor: 'pointer',
      width: props.fullWidth ? '100%' : 'auto',
      '& .MuiTypography-body1': { fontSize: props.small ? '14px' : undefined }
    }),
    center: {
      justifyContent: 'center',
    },
    fullWidth: {
      width: '100%',
    },
    label: (props:SwitchProps) => ({
      userSelect: 'none',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      cursor: 'pointer',
      flexShrink: 0,
    }),
    switch: (props:SwitchProps) => ({
      height: props.small ? '26px' : '32px',
      width: props.small ? '54px' : '60px',
      position: 'relative',
      transition: 'background-color 0.3s cubic-bezier(0, 1, 0.5, 1)',
      background: props.transparent ? 'transparent' : theme.palette.common.white,
      borderRadius: `18px`,
      borderColor: props.warning ? EdwardColors.WARNING : EdwardColors.GRAY_MEDIUM,
      borderWidth: '2px',
      borderStyle: 'solid',
      flexShrink: 0,

      '& span': {
        display: 'inline-block',
        width: props.small ? '20px' : '26px',
        height: props.small ? '20px' : '26px',
        lineHeight: props.small ? '20px' : '26px',
        position: 'absolute',
        textAlign: 'center',
        top: '1px',
        borderRadius: `18px`,
        borderWidth: 0,
        borderStyle: 'solid',
        overflow: 'hidden',
        transition:
          'transform 0.3s cubic-bezier(0, 1, 0.5, 1), -webkit-transform 0.3s cubic-bezier(0, 1, 0.5, 1)',
        transform: 'translate3d(1px, 0, 0)',

        '& svg': {
          position: 'relative',
          top: props.small ? '3px' : '4px',
          backgroundColor: 'transparent',
          color: '#fff',
          width: props.small ? '16px' : '20px',
          height: props.small ? '15px' : '19px'

        },
      },
      '& span:first-child': {
        color: '#fff',
        opacity: 1,
        background: EdwardColors.GRAY_MEDIUM
      },
      '& span:last-child': {
        color: '#fff',
        opacity: 0,
        background: EdwardColors.DARK
      },

    }),
    checkbox:(props:SwitchProps) => ({
      opacity: 0,
      position: 'absolute',
      top: 0,
      left: 0,
      '&:checked + $label $switch': {
        background: props.transparent ? 'transparent' : 'white',
        borderColor: theme.palette.primary.main,
        '& span': {
          transform: 'translate3d(29px, 0, 0)',
        },
        '& span:first-child': {
          opacity: 0
        },
        '& span:last-child': {
          opacity: 1
        }
      },
      '&:disabled + $label $switch': {
        borderColor: props.warning ? EdwardColors.WARNING : EdwardColors.GRAY_LIGHTER,
        '& span': {
          backgroundColor: props.warning ? EdwardColors.WARNING : EdwardColors.GRAY_LIGHTER,
        }
      },
    }),
    realLabel: {
      marginRight: theme.spacing(2),
      flexShrink: 1,
      flexGrow: 2,
    },
    disabled: (props:SwitchProps) => ({
      cursor: 'default',
      '& label': {
        cursor: 'default',
      },
      '& $switch': {
        borderColor: props.warning ? EdwardColors.WARNING : '#bdbdbd',
        '& span': {
          color: props.warning ? EdwardColors.WARNING :'#bdbdbd',
        },
        '&:after': {
          backgroundColor: props.warning ? EdwardColors.WARNING : '#bdbdbd',
        },
      },
    }),
    tooltip: {
      marginLeft: theme.spacing(2),
    },
    tooltipInside: {
      background: 'transparent !important',
      marginLeft: theme.spacing(0.5),
      '& .MuiSvgIcon-root': { position: 'relative', top: '4px'}
    },
    error: {
      color: theme.palette.error.main,
    },
    dropdownIcon: {
      color: EdwardColors.GRAY_MEDIUM,
      position: 'relative',
      top: '6px',
      left: '8px',
      transition: 'transform 0.2s'
    }
  }),
);

export const useTextSwitchStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props:SwitchProps) => ({
      position: 'relative',
      cursor: 'pointer',
      width: '100%',
      whiteSpace: 'nowrap',
    }),
    label: (props:SwitchProps) => ({
      userSelect: 'none',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      cursor: 'pointer',
      flexShrink: 0,
    }),
    switch: (props:SwitchProps) => ({
      width: '100%',
      height: '44px',
      position: 'relative',
      borderRadius: `12px`,
      flexShrink: 0,

      background: '#f5f5f5',
      boxShadow: 'inset 0 0 8px rgba(0,0,0,0.2)',
      fontWeight: 'bold',

      '& > span': {
        display: 'inline-flex',
        alignContent: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        width: '50%',
        height: '100%',
        textAlign: 'center',
        borderRadius: `12px`,
        position: 'relative',
        zIndex: 1

      },
      '&:before': {
        content: '""',
        width: '50%',
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        background: EdwardColors.DARK,
        borderRadius: '12px',
        transition: 'left 0.3s cubic-bezier(0, 1, 0.5, 1)',
        //transform: 'translate3d(1px, 0, 0)',

      }
    }),
    checkbox:(props:SwitchProps) => ({
      opacity: 0,
      position: 'absolute',
      top: 0,
      left: 0,
      '&:checked + $label $switch:before': {
          left: '50%'
        //background: props.transparent ? 'transparent' : 'white',
        //borderColor: theme.palette.primary.main,
        /*
        '& span': {
          transform: 'translate3d(29px, 0, 0)',
        },
        '& span:first-child': {
          opacity: 0
        },
        '& span:last-child': {
          opacity: 1
        }
        */
      },
      '&:disabled + $label $switch': {
        //borderColor: props.warning ? EdwardColors.WARNING : EdwardColors.GRAY_LIGHTER,
        '& span': {
          //backgroundColor: props.warning ? EdwardColors.WARNING : EdwardColors.GRAY_LIGHTER,
        }
      },
    }),
    realLabel: {
      marginRight: theme.spacing(2),
      flexShrink: 1,
      flexGrow: 2,
    },
    disabled: (props:SwitchProps) => ({
      cursor: 'default',
      '& div:before': {
        background: EdwardColors.GRAY_LIGHT,
      },
      '& label': {
        cursor: 'default',
      },
      '& $switch': {
        borderColor: props.warning ? EdwardColors.WARNING : '#bdbdbd',
        '& span': {
          color: props.warning ? EdwardColors.WARNING :EdwardColors.GRAY_MEDIUM,
        },
        '&:after': {
          backgroundColor: props.warning ? EdwardColors.WARNING : '#bdbdbd',
        },
      },
    }),
  }),
);

export const formPhoneFieldTheme: Theme = createMuiTheme(appTheme, {
  props: {
    MuiSelect: {
      variant: 'standard',
    },
    MuiButton: {
      variant: 'text',
    },
  },
});
